/**
 * Every GraphQL document the marketplace screen sends, built against the
 * contract in `parsina.bff.admin/src/graphql/typeDefs/marketplace.js`. One
 * file, so a contract change is a diff against one place and a test can match
 * a call by its operation name.
 *
 * Money fields are MINOR UNITS of the row's `currency` — whole rials for IRR,
 * which is also what a missing currency means; the screen renders them through
 * `formatMoney` in that currency. A settlement's lines are in its currency.
 */

const VENDOR_FIELDS = `
  clinicId slug displayName tagline description logoUrl city clinicType
  status marketplaceStatus marketplaceRejectionReason marketplaceReviewedAt
  commissionPct payoutAccountId contactPhone
  readiness { canActivate canRequestMarketplace missing }
  createdAt
`;

const LISTING_FIELDS = `
  id clinicId vendorSlug vendorName kind title description
  images { id url sortOrder alt }
  productType priceRial listPriceRial currency reviewStatus reviewNote reviewedAt
  visibility status sourceInactive updatedAt
`;

const SETTLEMENT_FIELDS = `
  id clinicId vendorName vendorSlug period grossRial commissionRial refundsRial netRial currency
  status paidAt paidReference payoutAccountId incomePosted
  lines { id orderId orderNumber kind grossRial commissionRial netRial at }
`;

export const VENDORS_QUERY = `
  query MarketplaceVendorRequests($status: String, $cursor: String, $limit: Int) {
    marketplaceVendorRequests(status: $status, cursor: $cursor, limit: $limit) {
      nextCursor items { ${VENDOR_FIELDS} }
    }
  }
`;

export const REVIEW_VENDOR = `
  mutation ReviewMarketplaceVendor($clinicId: ID!, $decision: MarketplaceVendorDecision!, $note: String) {
    reviewMarketplaceVendor(clinicId: $clinicId, decision: $decision, note: $note) { ${VENDOR_FIELDS} }
  }
`;

export const LISTINGS_QUERY = `
  query MarketplaceListingReviews($status: String, $cursor: String, $limit: Int) {
    marketplaceListingReviews(status: $status, cursor: $cursor, limit: $limit) {
      nextCursor total items { ${LISTING_FIELDS} }
    }
  }
`;

export const REVIEW_LISTING = `
  mutation ReviewMarketplaceListing($clinicId: ID!, $id: ID!, $decision: MarketplaceListingDecision!, $note: String) {
    reviewMarketplaceListing(clinicId: $clinicId, id: $id, decision: $decision, note: $note) { ${LISTING_FIELDS} }
  }
`;

export const SETTLEMENTS_QUERY = `
  query MarketplaceSettlementsAdmin($status: String, $period: String, $cursor: String, $limit: Int) {
    marketplaceSettlementsAdmin(status: $status, period: $period, cursor: $cursor, limit: $limit) {
      nextCursor items { ${SETTLEMENT_FIELDS} }
    }
  }
`;

export const CLOSE_SETTLEMENT = `
  mutation CloseMarketplaceSettlement($id: ID!) {
    closeMarketplaceSettlement(id: $id) { ${SETTLEMENT_FIELDS} }
  }
`;

export const MARK_SETTLEMENT_PAID = `
  mutation MarkMarketplaceSettlementPaid($id: ID!, $reference: String!) {
    markMarketplaceSettlementPaid(id: $id, reference: $reference) { ${SETTLEMENT_FIELDS} }
  }
`;

export const SETTINGS_QUERY = `
  query PlatformMarketplaceSettings {
    platformMarketplaceSettings { commissionPct updatedAt }
  }
`;

export const SET_COMMISSION = `
  mutation SetPlatformCommission($pct: Float!) {
    setPlatformCommission(pct: $pct) { commissionPct updatedAt }
  }
`;

export const CATEGORIES_QUERY = `
  query MarketplaceCategories {
    marketplaceCategories { id name parentId sortOrder }
  }
`;

export const UPSERT_CATEGORY = `
  mutation UpsertMarketplaceCategory($input: MarketplaceCategoryInput!) {
    upsertMarketplaceCategory(input: $input) { id name parentId sortOrder }
  }
`;
